import { RadioBox, TextInput } from 'lego-on-react';
import React from 'react';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/SmartHomeForm';
import block from 'propmods';

import { FormErrors } from '../../hocs/types';
import { routes } from '../../lib/routes';
import { AliceSkill, Channel, SmartHome } from '../../model/skill';
import { AliceSkillFormSettings, BackendType, SmartHomeFormSettings } from '../../types';
import { getIsYangoRequest } from '../../utils';
import Description from '../Form/Description/Description';
import { Field } from '../Form/Form';
import { FormError } from '../Form/FormError';
import { labels } from '../Settings/channels/labels';

import FunctionsContainer from './FunctionsContainer';
import { functionListErrorMessages, validateFunctionErrorMessages } from './texts';

import './BackendTypeField.scss';

const b = block('BackendTypeField');
const t = i18n(keyset);

interface Props<T extends AliceSkill | SmartHome> {
    onWebhookChange: (value: string) => void;
    onFunctionIdChange: (value: string) => void;
    onBackendTypeChange: (value: BackendType) => void;
    disabled?: boolean;
    errors: T extends AliceSkill ? FormErrors<AliceSkillFormSettings> : FormErrors<SmartHomeFormSettings>;
    values: T extends AliceSkill ? AliceSkillFormSettings : SmartHomeFormSettings;
    skill: T;
}

function BackendTypeField<T extends AliceSkill | SmartHome>({
    onWebhookChange,
    onFunctionIdChange,
    onBackendTypeChange,
    disabled,
    errors,
    values,
    skill,
}: Props<T>) {
    const l = labels[skill.channel];

    const objFieldProps = {
        webhook: {
            documentationUrl:
                skill.channel === Channel.AliceSkill
                    ? routes.documentation.aliceSkills.webhookUrl
                    : routes.documentation.smartHome.webhookUrl,
            error: errors.uri,
            caption: t('backend-caption'),
        },
        function: {
            documentationUrl: '',
            error: '',
            caption: '',
        },
    };

    const backendType = values.backendType;
    const fieldProps = objFieldProps[backendType];

    if (skill.channel === Channel.SmartHome && getIsYangoRequest()) {
        return (
            <Field
                required
                label={l.uri}
                error={errors.uri}
                caption={t('backend-caption')}
                documentationUrl={routes.documentation.smartHome.webhookUrl}
                name="uri"
            >
                <TextInput
                    theme="normal"
                    size="s"
                    text={values.uri}
                    type="url"
                    onChange={onWebhookChange}
                    disabled={disabled}
                />
            </Field>
        );
    }

    return (
        <div {...b()}>
            <Field
                required
                label="Backend"
                error={fieldProps.error as string}
                caption={fieldProps.caption}
                documentationUrl={fieldProps.documentationUrl}
                name="backend"
            >
                <div {...b('type')}>
                    <RadioBox
                        view="default"
                        tone="default"
                        theme="normal"
                        size="s"
                        name="backendType"
                        disabled={disabled}
                        value={backendType}
                        onChange={(e) => onBackendTypeChange(e.currentTarget.value as BackendType)}
                    >
                        <RadioBox.Radio value="webhook">{l.uri}</RadioBox.Radio>
                        <RadioBox.Radio value="function">{l.functionId}</RadioBox.Radio>
                    </RadioBox>
                </div>
                {backendType === 'webhook' ? (
                    <div {...b('webhook')}>
                        <TextInput
                            theme="normal"
                            size="s"
                            text={values.uri}
                            type="url"
                            onChange={onWebhookChange}
                            disabled={disabled}
                        />
                    </div>
                ) : (
                    <div {...b('functions')}>
                        <FunctionsContainer
                            onFunctionIdChange={onFunctionIdChange}
                            disabled={disabled}
                            value={values.functionId}
                            renderValidationError={(code) => (
                                <FormError offsetTop>
                                    <div {...b('functions-description')}>{validateFunctionErrorMessages[code]}</div>
                                </FormError>
                            )}
                            renderFunctionsListError={(code) => (
                                <Description>
                                    <div {...b('functions-status')}>{functionListErrorMessages[code]}</div>
                                </Description>
                            )}
                        />
                    </div>
                )}
            </Field>
        </div>
    );
}

export default BackendTypeField;
